import React, { createContext, useState, useCallback } from "react";
import axios from "axios";
import {toast} from "react-toastify";
import { getAuth } from "firebase/auth";
import {userManagementAuth} from "../../firebaseConfig";
export const UserContext = createContext();

export const UserContextProvider = ({ children }) => {
    const [users, setUsers] = useState([]);
    const [user, setUser] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [serverSuccess, setSeverSuccess] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [importedUsersToCount, setImportedUsersToCount] = useState(0);
    const [errorData, setErrorData] = useState(null);
    const [isSingleUserOperation, setIsSingleUserOperation] = useState(false); // New state

    const fetchAllUsers = useCallback(operatorId => {
        axios
            .post("/api/server/get-all-servers", { operatorId })
            .then(res => {
                setUsers([...res.data.allServers]);
            })
            .catch(err => {
                console.error("Error fetching all servers:", err);
            });
    }, []);

    const registerNewServers = (servers, operatorId, toastIdParam = null) => {
        // This is bulk import
        setIsSingleUserOperation(false);
        servers = servers.map(({location, ...rest }) => ({
            ...rest,
            operator_name: location,
        }));
        setIsSingleUserOperation(false);
        const toastId = toastIdParam || toast.loading('Adding staff members...', { position: "top-center" });

        return axios.post("/api/server/upload", {servers, operatorId})
            .then(response => {
                if (response.status === 200) {
                    // Success logic
                    fetchAllUsers(operatorId);
                    // Clear any previous error state
                    setErrorMessage(response.data.message);
                    setErrorData(response.data.errors || null);
                    setImportedUsersToCount(response.data.successful);
                    toast.update(toastId, {
                        render: `${response.data.successful} staff added successfully!`,
                        type: 'success',
                        isLoading: false,
                        autoClose: 5000,
                        closeButton: true
                    });
                    return { success: true, data: response.data };
                } else {
                    // Handle unsuccessful response
                    //create a CSV file using response.data.errors field
                    const errorData = response ? response.data : null;
                    createErrorCSV(errorData, toastId);
                    setImportedUsersToCount(response.data.successful || 0);
                    return { success: false, error: errorData };
                }
            })
            .catch(error => {
                const errorData = error.response ? error.response.data : null;
                if (errorData) {
                    createErrorCSV(errorData, toastId);
                } else {
                    const errorMsg = "Network error. Please try again.";
                    setErrorMessage(errorMsg);
                    toast.update(toastId, {
                        render: errorMsg,
                        type: 'error',
                        isLoading: false,
                        autoClose: 5000,
                        closeButton: true
                    });
                }
                return { success: false, error: errorData || error.message };
            });
    }

    const createErrorCSV = useCallback((errorResponse,toastIdParam = null ) => {
        const toastId = toastIdParam;
        if ("errors" in errorResponse) {
            setErrorMessage(errorResponse.message);
            setErrorData(errorResponse);

            let csvContent = "rowIndex,first_name,last_name,email,phone,location,Teams,Error\n";
            errorResponse.errors.forEach(errorObj => {
                let row = [];
                row.push(errorObj.rowIndex);
                row.push(errorObj.server.first_name || '');
                row.push(errorObj.server.last_name || '');
                row.push(errorObj.server.email || '');
                row.push(errorObj.server.phone || '');
                row.push(errorObj.server.operator_name || '');
                row.push(errorObj.server.teams || '');
                row.push((errorObj.message || '').replace(/,/g, ''));
                csvContent += row.join(',') + '\n';
            });

            // Create and download the file
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.setAttribute('href', url);
            const timestamp = new Date().toLocaleString('sv-SE');
            link.setAttribute('download', `upload_errors_${timestamp}.csv`);
            link.style.visibility = 'hidden';

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Clean up the URL object
            URL.revokeObjectURL(url);

            toast.update(toastId, {
                render: "Please check downloaded error file",
                type: 'error',
                isLoading: false,
                autoClose: 5000,
                closeButton: true
            });

        } else if ("errorMessage" in errorResponse) {
            setErrorMessage(errorResponse.errorMessage);
            toast.update(toastId, {
                render: errorResponse.errorMessage,
                type: 'error',
                isLoading: false,
                autoClose: 5000,
                closeButton: true
            });
        } else {
            const errorMsg = "An unknown error occurred. Please try again."
            setErrorMessage(errorMsg);
            toast.update(toastId, {
                render: errorMsg,
                type: 'error',
                isLoading: false,
                autoClose: 5000,
                closeButton: true
            });
        }
    }, []);

    const downloadErrorCSV = useCallback(() => {
        if (errorData) {
            createErrorCSV(errorData);
        }
    }, [errorData, createErrorCSV]);

    const registerNewServer = (server, operatorId, company, toastIdParam = null) => {
        // This is single user registration
        setIsSingleUserOperation(true);

        const { email, first_name, last_name, phone, location } = server;
        // Show loading toast
        const toastId = toastIdParam || toast.loading('Adding staff member...', {
            position: "top-center"
        });

        var servers = [{"first_name":first_name, "last_name":last_name, "email":email, "teams":"","phone": phone, "operator_name": location}];
        axios.post("/api/server/upload", {servers, operatorId}).then(response => {
            if(response.status === 200) {
                fetchAllUsers(operatorId);
                if (setSeverSuccess) setSeverSuccess(true);
                setImportedUsersToCount(1);
                setErrorMessage(null);
                setErrorData(null); // Clear any previous error data
                // Update to success toast
                toast.update(toastId, {
                    render: `${first_name} ${last_name} added successfully!`,
                    type: 'success',
                    isLoading: false,
                    autoClose: 5000,
                    closeButton: true
                });
            } else {
                const errorData = response ? response.data : null;
                const errorMsg = errorData?.errors?.[0]?.message || "Error adding user"
                setErrorMessage(errorMsg);
                setErrorData(errorData);
                setSeverSuccess(false);
                // Update to error toast
                toast.update(toastId, {
                    render: errorMsg,
                    type: 'error',
                    isLoading: false,
                    autoClose: 5000,
                    closeButton: true
                });
            }

        }).catch(error => {
            const errorData = error.response ? error.response.data : null;
            const errorMsg = errorData?.errors?.[0]?.message || "Error adding user";
            setErrorMessage(errorMsg);
            setErrorData(errorData);
            setSeverSuccess(false);
            // Update to error toast
            toast.update(toastId, {
                render: errorMsg,
                type: 'error',
                isLoading: false,
                autoClose: 5000,
                closeButton: true
            });
            console.error(error);
            throw error;
        });
    };


    async function getToken() {
        const user = userManagementAuth.currentUser;
        if (!user) throw new Error("No authenticated user");

        // Firebase ID token
        return await user.getIdToken(true);
    }


    const updateServerInfo = async newServerInfo => {
        const {
            _id,
            profileImg,
            first_name,
            last_name,
            email,
            gender,
            address,
            position,
            age,
            invitedDate,
            userRegistered,
            status,
        } = newServerInfo;
        const auth = getAuth();
        const user = auth.currentUser;

        if (!user) throw new Error("Not authenticated");

        const token = await getToken();

        try {
            await axios.patch("/api/server/update-server", {
                _id,
                profileImg,
                first_name,
                last_name,
                email: email.toLowerCase(),
                gender,
                address,
                position,
                age,
                invitedDate,
                userRegistered,
                status,
            }, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            await fetchUser(_id);
        } catch (err) {
            const message = err.response?.data?.error || err.message;
            throw new Error(message);
        }
        await fetchUser(_id);
    };

    const fetchUser = useCallback(serverId => {
        setErrorMessage(null);
        axios
            .get(`/api/server/get-server/${serverId}`)
            .then(res => {
                setUser(res.data.server);
                setErrorMessage(null);
            })
            .catch(err => setErrorMessage(err));
    }, []);

    const deleteSelectedServers = async servers => {
        const toastId = toast.loading('Deleting staff members...', { position: "top-center" });
        const serverIds = (servers || [])
            .map(server => (typeof server === "object" ? server?._id : server))
            .filter(serverId => serverId !== null && serverId !== undefined)
            .map(String);

        const results = await Promise.allSettled(
            serverIds.map(serverId =>
                axios.post("/api/server/delete-server", { serverId })
            )
        );

        const deletedIds = [];
        const failedIds = [];
        results.forEach((result, index) => {
            if (result.status === "fulfilled") {
                deletedIds.push(serverIds[index]);
            } else {
                failedIds.push(serverIds[index]);
                console.error("Error deleting staff member:", result.reason);
            }
        });

        if (deletedIds.length > 0) {
            const deletedIdSet = new Set(deletedIds);
            setUsers(currentUsers =>
                currentUsers.filter(server => !deletedIdSet.has(String(server?._id)))
            );
        }

        const render = failedIds.length === 0
            ? `${deletedIds.length} staff member${deletedIds.length === 1 ? "" : "s"} deleted successfully.`
            : `${deletedIds.length} deleted; ${failedIds.length} failed. Please retry the failed deletions.`;
        toast.update(toastId, {
            render,
            type: failedIds.length === 0 ? 'success' : 'error',
            isLoading: false,
            autoClose: 5000,
            closeButton: true
        });

        return { deletedIds, failedIds };
    };

    return (
        <UserContext.Provider
            value={{
                users,
                setUsers,
                setUser,
                user,
                isLoading,
                setIsLoading,
                registerNewServers,
                fetchAllUsers,
                fetchUser,
                registerNewServer,
                deleteSelectedServers,
                updateServerInfo,
                setErrorMessage,
                errorMessage,
                serverSuccess,
                setSeverSuccess,
                importedUsersToCount,
                setImportedUsersToCount,
                errorData,
                setErrorData,
                downloadErrorCSV,
                isSingleUserOperation,
                setIsSingleUserOperation,
            }}>
            {children}
        </UserContext.Provider>
    );
};
